import React, { useState, useEffect, useMemo } from 'react';
import { getDashboard, getWebhookAnalytics } from '../services/api';
import StatsCard from '../components/StatsCard';
import AfterPaySection from '../components/AfterPaySection';
import { DollarSign, CreditCard, TrendingUp, ShoppingCart, Target, Percent, Loader2, Building2, Filter, Clock, Zap, MessageCircle, AlertTriangle, Bell, X, ChevronDown, ChevronUp, ArrowUpRight, ArrowDownRight, Calendar, Briefcase, Smartphone, FileText, RotateCcw, Ban } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [period, setPeriod] = useState('today');
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [selectedBM, setSelectedBM] = useState('all');
  const [alerts, setAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(true);
  const [showAllCampaigns, setShowAllCampaigns] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'spend', direction: 'desc' });
  const [customDateStart, setCustomDateStart] = useState('');
  const [customDateEnd, setCustomDateEnd] = useState('');
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [webhookAnalytics, setWebhookAnalytics] = useState(null);
  const [webhookPeriod, setWebhookPeriod] = useState('this_month');
  const [webhookCustomStart, setWebhookCustomStart] = useState('');
  const [webhookCustomEnd, setWebhookCustomEnd] = useState('');
  const navigate = useNavigate();
  const [tokenWarnings, setTokenWarnings] = useState([]);
  const [salesUtmFilter, setSalesUtmFilter] = useState('all');

  const periods = [
    { value: 'today', label: 'Hoje' },
    { value: 'yesterday', label: 'Ontem' },
    { value: 'last_7d', label: 'Últimos 7 dias' },
    { value: 'last_14d', label: 'Últimos 14 dias' },
    { value: 'last_30d', label: 'Últimos 30 dias' },
    { value: 'this_month', label: 'Este mês' },
    { value: 'last_month', label: 'Mês passado' },
    { value: 'custom', label: 'Personalizado' },
  ];

  const fetchData = async () => {
    setLoading(true);
    try {
      let url = period;
      if (period === 'custom' && customDateStart && customDateEnd) {
        url = `custom&start=${customDateStart}&end=${customDateEnd}`;
      }
      const response = await getDashboard(url);
      setData(response.data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWebhookAnalytics = async (p) => {
    try {
      const per = p || webhookPeriod;
      const response = await getWebhookAnalytics(per);
      setWebhookAnalytics(response.data.analytics || null);
    } catch (error) {
      console.error('Erro webhook analytics:', error);
    }
  };

  const fetchTokenWarnings = async () => {
    try {
      const res = await api.get('/dashboard/token-expiration');
      setTokenWarnings(res.data.warnings || []);
    } catch(e) {}
  };

  const fetchAlerts = async () => {
    try {
      const response = await api.get('/whatsapp/alerts?days=7');
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Erro alertas:', error);
    }
  };

  const forceSync = async () => {
    setSyncing(true);
    try {
      await api.post('/dashboard/sync');
      // Polling: verificar a cada 2s até cacheAge < 2 min (sync completou)
      let attempts = 0;
      const poll = async () => {
        attempts++;
        try {
          const res = await getDashboard(period === 'custom' ? `custom&start=${customDateStart}&end=${customDateEnd}` : period);
          const newCacheAge = res.data?.cacheAge;
          if (newCacheAge !== null && newCacheAge < 2) {
            setData(res.data);
            setSyncing(false);
            return;
          }
        } catch(e) {}
        if (attempts < 15) {
          setTimeout(poll, 2000);
        } else {
          // Timeout: carregar o que tiver
          fetchData();
          setSyncing(false);
        }
      };
      setTimeout(poll, 3000);
    } catch (error) {
      console.error('Erro ao sincronizar:', error);
      setSyncing(false);
    }
  };

  useEffect(() => { 
    if (period !== 'custom') {
      fetchData(); 
    }
    fetchAlerts();
    fetchTokenWarnings();
    fetchWebhookAnalytics();
  }, [period]);

  useEffect(() => {
    if (period === 'custom' && customDateStart && customDateEnd) {
      fetchData();
    }
  }, [customDateStart, customDateEnd]);

  useEffect(() => {
    setShowAllCampaigns(false);
  }, [selectedAccount, selectedBM]);

  useEffect(() => {
    if (webhookPeriod === 'custom' && webhookCustomStart && webhookCustomEnd) {
      fetchWebhookAnalytics(webhookCustomStart + ',' + webhookCustomEnd);
    } else if (webhookPeriod !== 'custom') {
      fetchWebhookAnalytics(webhookPeriod);
    }
  }, [webhookPeriod, webhookCustomStart, webhookCustomEnd]);

  // Dados extraídos (com fallback para arrays vazios)
  const summary = data?.summary || {};
  const chartData = data?.chartData || [];
  const rawDbAccounts = data?.dbAccounts || [];
  const cacheAccounts = data?.accounts || [];
  const allBMs = data?.dbBMs || [];
  
  // Merge: usar dbAccounts como base, enriquecer com spend do cache
  const accounts = useMemo(() => {
    if (rawDbAccounts.length === 0) return cacheAccounts;
    const spendMap = {};
    cacheAccounts.forEach(a => { spendMap[a.id] = a; });
    return rawDbAccounts.map(a => ({
      id: a.id,
      name: a.adAccountName || a.name || a.id,
      businessManagerId: a.businessManagerId,
      businessManagerName: a.bmName,
      bmName: a.bmName,
      spend: spendMap[a.id]?.spend || 0,
      currency: spendMap[a.id]?.currency || 'BRL'
    }));
  }, [rawDbAccounts, cacheAccounts]);
  const cacheAge = data?.cacheAge;
  const yesterdaySameTime = data?.yesterdaySameTime || {};
  const allCampaigns = data?.campaigns || [];

  // TODOS os useMemo ANTES de qualquer return condicional
  const businessManagers = useMemo(() => {
    if (allBMs.length > 0) {
      return allBMs.map(bm => ({ id: bm.bmId, name: bm.bmName }));
    }
    const bms = new Map();
    accounts.forEach(acc => {
      if (acc.businessManagerId && (acc.businessManagerName || acc.bmName)) {
        bms.set(acc.businessManagerId, acc.businessManagerName || acc.bmName);
      }
    });
    return Array.from(bms, ([id, name]) => ({ id, name }));
  }, [accounts, allBMs]);

  const filteredAccounts = useMemo(() => {
    if (selectedBM === 'all') return accounts;
    return accounts.filter(acc => acc.businessManagerId === selectedBM);
  }, [accounts, selectedBM]);

  const campaigns = useMemo(() => {
    let filtered = allCampaigns;
    if (selectedBM !== 'all') {
      const bmAccountIds = filteredAccounts.map(a => a.id);
      filtered = filtered.filter(c => bmAccountIds.includes(c.adAccountId));
    }
    if (selectedAccount !== 'all') {
      filtered = filtered.filter(c => c.adAccountId === selectedAccount);
    }
    return filtered;
  }, [allCampaigns, selectedAccount, selectedBM, filteredAccounts]);

  const filteredSummary = useMemo(() => {
    if (selectedAccount === 'all' && selectedBM === 'all') return summary;
    return {
      ...summary,
      totalSpend: campaigns.reduce((sum, c) => sum + (c.spend || 0), 0),
      totalMessages: campaigns.reduce((sum, c) => sum + (c.messagesStarted || 0), 0),
    };
  }, [summary, campaigns, selectedAccount, selectedBM]);

  const sortedCampaigns = useMemo(() => {
    // Primeiro ordenar por status (ACTIVE primeiro), depois pelo critério selecionado
    const sorted = [...campaigns];
    sorted.sort((a, b) => {
      // Ativas primeiro
      if (a.status === 'ACTIVE' && b.status !== 'ACTIVE') return -1;
      if (a.status !== 'ACTIVE' && b.status === 'ACTIVE') return 1;
      // Depois ordenar pelo critério selecionado
      let aVal = a[sortConfig.key] || 0;
      let bVal = b[sortConfig.key] || 0;
      if (typeof aVal === 'string') {
        return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return sorted;
  }, [campaigns, sortConfig]);

  const displayedCampaigns = showAllCampaigns ? sortedCampaigns : sortedCampaigns.slice(0, 20);

  // Cálculos derivados
  const realProfit = summary.totalProducerCommission || 0;
  const netProfit = realProfit - (filteredSummary.totalSpend || 0);
  const roas = filteredSummary.totalSpend > 0 ? (filteredSummary.totalRevenue || 0) / filteredSummary.totalSpend : 0;
  const roi = filteredSummary.totalSpend > 0 ? (netProfit / filteredSummary.totalSpend) * 100 : 0;
  const totalMessages = filteredSummary.totalMessages || summary.totalMessages || 0;
  const totalSpend = filteredSummary.totalSpend || 0;
  const costPerMessage = totalMessages > 0 ? totalSpend / totalMessages : 0;
  const unreadAlerts = alerts.filter(a => !a.readAt);
  const todayStr = new Date().toISOString().split('T')[0];
  const criticalAlerts = unreadAlerts.filter(a => a.alertType === 'CRITICAL' && a.createdAt && a.createdAt.split('T')[0] === todayStr);

  // Funções helper
  const formatCacheAge = (minutes) => {
    if (minutes === null || minutes === undefined) return 'Nunca sincronizado';
    if (minutes < 1) return 'Agora mesmo';
    if (minutes === 1) return '1 minuto atrás';
    if (minutes < 60) return `${minutes} minutos atrás`;
    const hours = Math.floor(minutes / 60);
    if (hours === 1) return '1 hora atrás';
    return `${hours} horas atrás`;
  };

  const calcVariation = (current, yesterday) => {
    if (!yesterday || yesterday === 0) return null;
    return ((current - yesterday) / yesterday) * 100;
  };

  const VariationBadge = ({ current, yesterday }) => {
    const variation = calcVariation(current, yesterday);
    if (variation === null) return null;
    const isPositive = variation >= 0;
    return (
      <span className={`flex items-center text-xs ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
        {isPositive ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
        {Math.abs(variation).toFixed(1)}%
      </span>
    );
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  // AGORA sim o loading check
  if (loading) {
    return <div className="flex items-center justify-center h-full"><Loader2 className="animate-spin text-blue-500" size={40} /></div>;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-gray-400">Visão geral do seu desempenho</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-gray-400 text-sm bg-gray-800/50 px-3 py-2 rounded-lg">
            <Clock size={14} />
            <span>{formatCacheAge(cacheAge)}</span>
          </div>
          
          <button 
            onClick={forceSync} 
            disabled={syncing}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              syncing 
                ? 'bg-yellow-600/50 text-yellow-300 cursor-not-allowed' 
                : 'bg-yellow-600 text-white hover:bg-yellow-500'
            }`}
          >
            {syncing ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Sincronizando...
              </>
            ) : (
              <>
                <Zap size={16} />
                Atualizar Meta
              </>
            )}
          </button>

          {/* Filtro de BM */}
          {businessManagers.length > 0 && (
            <div className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2">
              <Briefcase size={16} className="text-gray-400" />
              <select 
                value={selectedBM} 
                onChange={(e) => {
                  setSelectedBM(e.target.value);
                  setSelectedAccount('all');
                }} 
                className="bg-transparent text-white text-sm outline-none cursor-pointer"
              >
                <option value="all" className="bg-gray-800">Todas as BMs</option>
                {businessManagers.map((bm) => (
                  <option key={bm.id} value={bm.id} className="bg-gray-800">
                    {bm.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {/* Filtro de Contas */}
          {filteredAccounts.length > 0 && (
            <div className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2">
              <Filter size={16} className="text-gray-400" />
              <select 
                value={selectedAccount} 
                onChange={(e) => setSelectedAccount(e.target.value)} 
                className="bg-transparent text-white text-sm outline-none cursor-pointer"
              >
                <option value="all" className="bg-gray-800">Todas as contas ({filteredAccounts.length})</option>
                {filteredAccounts.map((acc) => (
                  <option key={acc.id} value={acc.id} className="bg-gray-800">
                    {acc.name} (R$ {acc.spend?.toFixed(0)})
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {/* Filtro de Período */}
          <div className="flex items-center gap-2">
            <select 
              value={period} 
              onChange={(e) => {
                setPeriod(e.target.value);
                setShowCustomDate(e.target.value === 'custom');
              }} 
              className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
            >
              {periods.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            
            {showCustomDate && (
              <div className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg px-3 py-1">
                <Calendar size={14} className="text-gray-400" />
                <input 
                  type="date" 
                  value={customDateStart}
                  onChange={(e) => setCustomDateStart(e.target.value)}
                  className="bg-transparent text-white text-sm outline-none"
                />
                <span className="text-gray-500">até</span>
                <input 
                  type="date" 
                  value={customDateEnd}
                  onChange={(e) => setCustomDateEnd(e.target.value)}
                  className="bg-transparent text-white text-sm outline-none"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Token Expirando */}
      {tokenWarnings.length > 0 && (
        <div className="bg-orange-900/30 border border-orange-500/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="text-orange-400" size={20} />
            <h2 className="text-orange-400 font-semibold">Token expirando!</h2>
          </div>
          {tokenWarnings.map((w, i) => (
            <div key={i} className="flex items-center justify-between bg-orange-950/50 rounded-lg p-3 mb-1">
              <span className="text-white text-sm"><strong>{w.bmName}</strong> — {w.daysLeft <= 0 ? 'EXPIRADO!' : `expira em ${w.daysLeft} dia${w.daysLeft > 1 ? 's' : ''}`}</span>
              <button onClick={() => navigate('/settings')} className="text-orange-400 hover:text-orange-300 text-sm">Renovar →</button>
            </div>
          ))}
        </div>
      )}

      {/* Alertas Flutuantes */}
      {criticalAlerts.length > 0 && showAlerts && (
        <div className="fixed bottom-4 right-4 z-50 space-y-2 max-w-sm">
          {criticalAlerts.slice(0, 3).map((alert, i) => (
            <div key={i} className="bg-red-950/95 border border-red-500/50 rounded-lg p-3 shadow-lg backdrop-blur-sm animate-slide-up flex items-start gap-3">
              <AlertTriangle className="text-red-400 flex-shrink-0 mt-0.5" size={16} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-white text-sm font-medium truncate">{alert.chipName}</span>
                  <span className="text-red-300 text-xs">{alert.phoneNumber}</span>
                </div>
                <p className="text-red-400/80 text-xs mt-0.5 truncate">{alert.message}</p>
              </div>
              <button onClick={() => setShowAlerts(false)} className="text-red-400/60 hover:text-red-300 flex-shrink-0"><X size={14} /></button>
            </div>
          ))}
          <button onClick={() => navigate('/whatsapp-health')} className="w-full text-center text-xs text-red-400 hover:text-red-300 py-1">Ver detalhes →</button>
        </div>
      )}

      {/* Cards Principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-900/50 to-green-800/30 rounded-xl p-5 border border-green-700/30">
          <div className="flex items-center justify-between">
            <span className="text-green-400 text-sm">Faturamento</span>
            <DollarSign className="text-green-400" size={20} />
          </div>
          <div className="mt-2">
            <span className="text-2xl font-bold text-white">R$ {(summary.totalRevenue || filteredSummary.totalRevenue || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
            <VariationBadge current={filteredSummary.totalRevenue} yesterday={yesterdaySameTime.revenue} />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 rounded-xl p-5 border border-blue-700/30">
          <div className="flex items-center justify-between">
            <span className="text-blue-400 text-sm">Investido</span>
            <CreditCard className="text-blue-400" size={20} />
          </div>
          <div className="mt-2">
            <span className="text-2xl font-bold text-white">R$ {(filteredSummary.totalSpend || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
            <VariationBadge current={filteredSummary.totalSpend} yesterday={yesterdaySameTime.spend} />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-purple-900/50 to-purple-800/30 rounded-xl p-5 border border-purple-700/30">
          <div className="flex items-center justify-between">
            <span className="text-purple-400 text-sm">Comissão</span>
            <TrendingUp className="text-purple-400" size={20} />
          </div>
          <div className="mt-2">
            <span className="text-2xl font-bold text-white">R$ {realProfit.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
            <VariationBadge current={realProfit} yesterday={yesterdaySameTime.commission} />
          </div>
        </div>
        
        <div className={`bg-gradient-to-br ${netProfit >= 0 ? 'from-green-900/50 to-green-800/30 border-green-700/30' : 'from-red-900/50 to-red-800/30 border-red-700/30'} rounded-xl p-5 border`}>
          <div className="flex items-center justify-between">
            <span className={netProfit >= 0 ? 'text-green-400 text-sm' : 'text-red-400 text-sm'}>Lucro Líquido</span>
            <Target className={netProfit >= 0 ? 'text-green-400' : 'text-red-400'} size={20} />
          </div>
          <div className="mt-2">
            <span className="text-2xl font-bold text-white">R$ {netProfit.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
            <VariationBadge current={netProfit} yesterday={yesterdaySameTime.profit} />
          </div>
        </div>
      </div>

      {/* Cards Secundários */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        <div className="bg-gray-800 rounded-xl p-4">
          <span className="text-gray-400 text-xs">Vendas</span>
          <div className="text-xl font-bold text-white mt-1">{summary.totalSales || 0}</div>
          <VariationBadge current={summary.totalSales} yesterday={yesterdaySameTime.sales} />
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <span className="text-gray-400 text-xs">Ticket Médio</span>
          <div className="text-xl font-bold text-white mt-1">R$ {(summary.avgTicket || 0).toLocaleString('pt-BR', {minimumFractionDigits:2, maximumFractionDigits:2})}</div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <span className="text-gray-400 text-xs">CPA</span>
          <div className="text-xl font-bold text-yellow-400 mt-1">R$ {(filteredSummary.totalSpend > 0 ? filteredSummary.totalSpend / (summary.totalSales || 1) : 0).toLocaleString('pt-BR', {minimumFractionDigits:2, maximumFractionDigits:2})}</div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <span className="text-gray-400 text-xs">Taxa Tracking</span>
          <div className={`text-xl font-bold mt-1 ${(summary.trackingRate || 0) >= 70 ? 'text-green-400' : 'text-yellow-400'}`}>{(summary.trackingRate || 0).toFixed(1)}%</div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <span className="text-gray-400 text-xs">Mensagens</span>
          <div className="text-xl font-bold text-green-400 mt-1">{totalMessages.toLocaleString()}</div>
          <VariationBadge current={totalMessages} yesterday={yesterdaySameTime.messages} />
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <span className="text-gray-400 text-xs">Custo/Msg</span>
          <div className="text-xl font-bold text-orange-400 mt-1">R$ {costPerMessage.toLocaleString('pt-BR', {minimumFractionDigits:2, maximumFractionDigits:2})}</div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <span className="text-gray-400 text-xs">ROAS</span>
          <div className={`text-xl font-bold mt-1 ${roas >= 1 ? 'text-green-400' : 'text-red-400'}`}>{roas.toFixed(2)}x</div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <span className="text-gray-400 text-xs">ROI</span>
          <div className={`text-xl font-bold mt-1 ${roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>{roi.toFixed(1)}%</div>
        </div>
      </div>

      {/* After Pay (revisao 15/07) */}
      <AfterPaySection period={period} />

      {/* Webhook Analytics */}
      {webhookAnalytics && (
        <div className="bg-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div><h2 className="text-lg font-bold text-white">Eventos de Pagamento Frustrados</h2><p className="text-gray-400 text-sm">Dinheiro na mesa</p></div>
            <div className="flex items-center gap-2">
              <select value={webhookPeriod} onChange={(e) => { setWebhookPeriod(e.target.value); }} className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm">
                <option value="this_month">Este mês</option>
                <option value="last_30d">Últimos 30 dias</option>
                <option value="last_7d">Últimos 7 dias</option>
                <option value="custom">Personalizado</option>
              </select>
              {webhookPeriod === 'custom' && (
                <div className="flex items-center gap-1">
                  <input type="date" value={webhookCustomStart} onChange={(e) => setWebhookCustomStart(e.target.value)} className="bg-gray-900 border border-gray-700 rounded-lg px-2 py-1.5 text-white text-sm" />
                  <span className="text-gray-500 text-sm">a</span>
                  <input type="date" value={webhookCustomEnd} onChange={(e) => setWebhookCustomEnd(e.target.value)} className="bg-gray-900 border border-gray-700 rounded-lg px-2 py-1.5 text-white text-sm" />
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="bg-red-900/20 border border-red-700/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2"><AlertTriangle size={16} className="text-red-400" /><span className="text-red-400 text-xs">Chargeback</span></div>
              <p className="text-xl font-bold text-white">{webhookAnalytics.chargeback?.count || 0}</p>
              {(webhookAnalytics.chargeback?.value || 0) > 0 && <p className="text-red-400 text-xs mt-1">R$ {webhookAnalytics.chargeback.value.toFixed(2)}</p>}
            </div>
            <div className="bg-orange-900/20 border border-orange-700/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2"><RotateCcw size={16} className="text-orange-400" /><span className="text-orange-400 text-xs">Reembolso</span></div>
              <p className="text-xl font-bold text-white">{webhookAnalytics.refund?.count || 0}</p>
              {(webhookAnalytics.refund?.value || 0) > 0 && <p className="text-orange-400 text-xs mt-1">R$ {webhookAnalytics.refund.value.toFixed(2)}</p>}
            </div>
            <div className="bg-yellow-900/20 border border-yellow-700/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2"><ShoppingCart size={16} className="text-yellow-400" /><span className="text-yellow-400 text-xs">Carrinho Abandonado</span></div>
              <p className="text-xl font-bold text-white">{webhookAnalytics.abandoned?.count || 0}</p>
              {(webhookAnalytics.abandoned?.value || 0) > 0 && <p className="text-yellow-400 text-xs mt-1">R$ {webhookAnalytics.abandoned.value.toFixed(2)}</p>}
            </div>
            <div className="bg-cyan-900/20 border border-cyan-700/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2"><Smartphone size={16} className="text-cyan-400" /><span className="text-cyan-400 text-xs">Pix Expirado</span></div>
              <p className="text-xl font-bold text-white">{webhookAnalytics.pix_expired?.count || webhookAnalytics.pix_generated?.count || 0}</p>
              {((webhookAnalytics.pix_expired?.value || webhookAnalytics.pix_generated?.value) || 0) > 0 && <p className="text-cyan-400 text-xs mt-1">R$ {(webhookAnalytics.pix_expired?.value || webhookAnalytics.pix_generated?.value || 0).toFixed(2)}</p>}
            </div>
            <div className="bg-blue-900/20 border border-blue-700/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2"><FileText size={16} className="text-blue-400" /><span className="text-blue-400 text-xs">Boleto Vencido</span></div>
              <p className="text-xl font-bold text-white">{webhookAnalytics.boleto_expired?.count || 0}</p>
              {(webhookAnalytics.boleto_expired?.value || 0) > 0 && <p className="text-blue-400 text-xs mt-1">R$ {webhookAnalytics.boleto_expired.value.toFixed(2)}</p>}
            </div>
            <div className="bg-pink-900/20 border border-pink-700/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2"><Ban size={16} className="text-pink-400" /><span className="text-pink-400 text-xs">Cartão Recusado</span></div>
              <p className="text-xl font-bold text-white">{webhookAnalytics.card_refused?.count || 0}</p>
              {(webhookAnalytics.card_refused?.value || 0) > 0 && <p className="text-pink-400 text-xs mt-1">R$ {webhookAnalytics.card_refused.value.toFixed(2)}</p>}
            </div>
          </div>

          {/* Total perdido + Motivos de recusa */}
          {(webhookAnalytics.totals || webhookAnalytics.topRefusalReasons) && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
              {webhookAnalytics.totals && (
                <div className="bg-gradient-to-br from-red-900/30 to-orange-900/20 border border-red-700/40 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2"><AlertTriangle size={18} className="text-red-400" /><span className="text-red-300 text-sm font-medium">Total Perdido</span></div>
                  <p className="text-2xl font-bold text-white">R$ {(webhookAnalytics.totals.totalLost || 0).toFixed(2)}</p>
                  <p className="text-red-400 text-xs mt-1">{webhookAnalytics.totals.totalLostCount || 0} eventos frustrados no período</p>
                </div>
              )}
              {Array.isArray(webhookAnalytics.topRefusalReasons) && webhookAnalytics.topRefusalReasons.length > 0 && (
                <div className="bg-gray-900/40 border border-gray-700 rounded-xl p-4 md:col-span-2">
                  <div className="flex items-center gap-2 mb-3"><Ban size={16} className="text-pink-400" /><span className="text-white text-sm font-medium">Top motivos de recusa (cartão)</span></div>
                  <div className="space-y-2">
                    {(() => {
                      const reasons = webhookAnalytics.topRefusalReasons;
                      const maxCount = Math.max(...reasons.map(r => r.count));
                      return reasons.map((r, i) => {
                        const pct = maxCount > 0 ? (r.count / maxCount) * 100 : 0;
                        return (
                          <div key={i} className="flex items-center gap-2 text-xs">
                            <span className="text-gray-300 flex-1 truncate" title={r.reason}>{r.reason}</span>
                            <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                              <div className="h-full bg-pink-500 rounded-full" style={{ width: pct + '%' }}></div>
                            </div>
                            <span className="text-pink-400 font-medium w-6 text-right">{r.count}</span>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}
              {(!Array.isArray(webhookAnalytics.topRefusalReasons) || webhookAnalytics.topRefusalReasons.length === 0) && webhookAnalytics.totals && (
                <div className="bg-gray-900/40 border border-gray-700 rounded-xl p-4 md:col-span-2 flex items-center justify-center">
                  <p className="text-gray-500 text-sm">Sem recusas de cartão no período</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Gráfico */}
      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-bold text-white mb-4">Faturamento x Investimento x Lucro</h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="date" stroke="#9CA3AF" tickFormatter={(v) => { const d = new Date(v); return `${d.getDate()}/${d.getMonth()+1}`; }} />
            <YAxis stroke="#9CA3AF" tickFormatter={(v) => v >= 1000 ? `R$ ${(v/1000).toFixed(1)}k` : `R$ ${v.toFixed(0)}`} width={70} />
            <Tooltip 
              contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px' }} 
              formatter={(v, name) => [`R$ ${v.toFixed(2)}`, name]} 
              labelFormatter={(v) => { const d = new Date(v); return `${d.getDate()}/${d.getMonth()+1}/${d.getFullYear()}`; }}
            />
            <Legend />
            <Line type="monotone" dataKey="revenue" name="Faturamento" stroke="#10B981" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="spend" name="Investido" stroke="#3B82F6" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="commission" name="Comissão" stroke="#8B5CF6" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Tabela de Campanhas */}
      <div className="bg-gray-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white">
            Campanhas 
            {(selectedAccount !== 'all' || selectedBM !== 'all') && (
              <span className="text-sm font-normal text-gray-400 ml-2">(Filtrado)</span>
            )}
          </h2>
          <span className="text-gray-400 text-sm">{campaigns.length} campanhas</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full table-fixed">
            <thead>
              <tr className="text-left text-gray-400 text-xs border-b border-gray-700">
                <th className="pb-3 cursor-pointer hover:text-white min-w-[200px] w-[250px]" onClick={() => handleSort('campaignName')}>
                  <div className="flex items-center gap-1">Campanha <SortIcon columnKey="campaignName" /></div>
                </th>
                <th className="pb-3 cursor-pointer hover:text-white min-w-[80px] w-[100px]" onClick={() => handleSort('adAccountName')}>
                  <div className="flex items-center gap-1">Conta <SortIcon columnKey="adAccountName" /></div>
                </th>
                <th className="pb-3 cursor-pointer hover:text-white text-center" onClick={() => handleSort('status')}>
                  <div className="flex items-center justify-center gap-1">Veiculação <SortIcon columnKey="status" /></div>
                </th>
                <th className="pb-3 cursor-pointer hover:text-white text-right" onClick={() => handleSort('dailyBudget')}>
                  <div className="flex items-center justify-end gap-1">Orçamento <SortIcon columnKey="dailyBudget" /></div>
                </th>
                <th className="pb-3 cursor-pointer hover:text-white text-right" onClick={() => handleSort('spend')}>
                  <div className="flex items-center justify-end gap-1">Investido <SortIcon columnKey="spend" /></div>
                </th>
                <th className="pb-3 cursor-pointer hover:text-white text-right" onClick={() => handleSort('sales')}>
                  <div className="flex items-center justify-end gap-1">Vendas <SortIcon columnKey="sales" /></div>
                </th>
                <th className="pb-3 text-right">Custo/Venda</th>
                <th className="pb-3 cursor-pointer hover:text-white text-right" onClick={() => handleSort('messagesStarted')}>
                  <div className="flex items-center justify-end gap-1">Msgs <SortIcon columnKey="messagesStarted" /></div>
                </th>
                <th className="pb-3 text-right">Custo/Msg</th>
                <th className="pb-3 cursor-pointer hover:text-white text-right" onClick={() => handleSort('clicks')}>
                  <div className="flex items-center justify-end gap-1">Cliques <SortIcon columnKey="clicks" /></div>
                </th>
                <th className="pb-3 cursor-pointer hover:text-white text-right" onClick={() => handleSort('ctr')}>
                  <div className="flex items-center justify-end gap-1">CTR <SortIcon columnKey="ctr" /></div>
                </th>
                <th className="pb-3 cursor-pointer hover:text-white text-right" onClick={() => handleSort('cpm')}>
                  <div className="flex items-center justify-end gap-1">CPM <SortIcon columnKey="cpm" /></div>
                </th>
              </tr>
            </thead>
            <tbody>
              {displayedCampaigns.length === 0 ? (
                <tr><td colSpan="12" className="py-8 text-center text-gray-500">Nenhuma campanha encontrada</td></tr>
              ) : (
                displayedCampaigns.map((c, i) => (
                  <tr key={i} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                    <td className="py-3 text-white font-medium text-sm">{c.campaignName}</td>
                    <td className="py-3"><span className="text-xs text-gray-500 bg-gray-700/50 px-2 py-1 rounded">{c.adAccountName}</span></td>
                    <td className="py-3 text-center">
                      <span className={`text-xs px-2 py-1 rounded ${c.status === 'ACTIVE' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                        {c.status === 'ACTIVE' ? 'Ativo' : 'Desativada'}
                      </span>
                    </td>
                    <td className="py-3 text-right text-gray-400 text-sm">{c.dailyBudget ? `R$ ${(c.dailyBudget / 100).toFixed(0)}` : '-'}</td>
                    <td className="py-3 text-right text-blue-400 font-medium text-sm">R$ {(c.spend || 0).toFixed(2)}</td>
                    <td className="py-3 text-right text-purple-400 font-medium text-sm">{c.sales || 0}</td>
                    <td className="py-3 text-right text-yellow-400 text-sm">R$ {c.sales > 0 ? (c.spend / c.sales).toFixed(2) : '0.00'}</td>
                    <td className="py-3 text-right text-green-400 font-medium text-sm">{c.messagesStarted || 0}</td>
                    <td className="py-3 text-right text-orange-400 text-sm">R$ {c.messagesStarted > 0 ? (c.spend / c.messagesStarted).toFixed(2) : '0.00'}</td>
                    <td className="py-3 text-right text-gray-400 text-sm">{(c.clicks || 0).toLocaleString()}</td>
                    <td className="py-3 text-right"><span className={`text-sm ${(c.ctr || 0) >= 1 ? 'text-green-400' : 'text-yellow-400'}`}>{(c.ctr || 0).toFixed(2)}%</span></td>
                    <td className="py-3 text-right text-gray-400 text-sm">R$ {(c.cpm || 0).toFixed(2)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {sortedCampaigns.length > 20 && (
          <div className="mt-4 text-center">
            <button onClick={() => setShowAllCampaigns(!showAllCampaigns)} className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1 mx-auto">
              {showAllCampaigns ? <>Mostrar menos <ChevronUp size={16} /></> : <>Ver mais ({sortedCampaigns.length - 20} campanhas) <ChevronDown size={16} /></>}
            </button>
          </div>
        )}
      </div>

      {/* Últimas Vendas */}
      {data?.recentSales?.length > 0 && (() => {
        const utmOptions = [...new Set(data.recentSales.map(s => s.utmMedium).filter(Boolean))];
        const filtered = salesUtmFilter === 'all' ? data.recentSales : data.recentSales.filter(s => s.utmMedium === salesUtmFilter);
        return (
        <div className="bg-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-white">Últimas Vendas</h2>
            <select value={salesUtmFilter} onChange={(e) => setSalesUtmFilter(e.target.value)} className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm">
              <option value="all">Todas UTMs</option>
              {utmOptions.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-gray-400 text-xs border-b border-gray-700">
                  <th className="pb-3">Cliente</th>
                  <th className="pb-3">Produto</th>
                  <th className="pb-3">Vendedor</th>
                  <th className="pb-3">Status</th>
                  <th className="pb-3">Origem</th>
                  <th className="pb-3">UTM</th>
                  <th className="pb-3 text-right">Valor</th>
                  <th className="pb-3 text-right">Comissão</th>
                  <th className="pb-3 text-center">Track</th>
                  <th className="pb-3">Data</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((sale, i) => (
                  <tr key={i} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                    <td className="py-3 text-white text-sm">{sale.customerName}</td>
                    <td className="py-3 text-gray-400 text-xs max-w-[120px] truncate">{sale.productName}</td>
                    <td className="py-3"><span className={sale.sellerName ? 'text-cyan-400 text-sm' : 'text-gray-600 text-sm'}>{sale.sellerName || '-'}</span></td>
                    <td className="py-3">{(() => { const st = sale.paymentStatus || ''; if (st.includes('approv')) return <span className="text-green-400 bg-green-400/10 px-2 py-0.5 rounded text-xs">Aprovado</span>; if (st.includes('charge')) return <span className="text-red-400 bg-red-400/10 px-2 py-0.5 rounded text-xs">Chargeback</span>; if (st.includes('refund')) return <span className="text-orange-400 bg-orange-400/10 px-2 py-0.5 rounded text-xs">Reembolsado</span>; if (st.includes('wait')) return <span className="text-yellow-400 bg-yellow-400/10 px-2 py-0.5 rounded text-xs">Aguardando</span>; return <span className="text-gray-500 text-xs">{st || '-'}</span>; })()}</td>
                    <td className="py-3"><span className="text-xs bg-gray-700 px-2 py-1 rounded text-gray-300">{sale.platform || 'N/A'}</span></td>
                    <td className="py-3 text-gray-500 text-xs">{sale.utmMedium || '-'}</td>
                    <td className="py-3 text-right text-green-400 font-medium text-sm">R$ {parseFloat(sale.value).toFixed(2)}</td>
                    <td className="py-3 text-right text-purple-400 font-medium text-sm">R$ {parseFloat(sale.producerCommission || 0).toFixed(2)}</td>
                    <td className="py-3 text-center">
                      {sale.trackingFound ? <span className="text-green-400 bg-green-400/20 px-2 py-1 rounded text-xs">✓</span> : <span className="text-red-400 bg-red-400/20 px-2 py-1 rounded text-xs">✗</span>}
                    </td>
                    <td className="py-3 text-gray-400 text-xs">{new Date(sale.createdAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default Dashboard;
